// Release notes shown in the "Что нового" panel. Data-driven: add a new entry
// to the top of CHANGELOG for each release and bump the version. The newest
// version drives the "unseen update" badge in the sidebar.

export interface Release {
  version: string;
  date: string; // human label, e.g. "июль 2026"
  title: string;
  items: string[];
}

export const CHANGELOG: Release[] = [
  {
    version: "0.7.0",
    date: "сентябрь 2026",
    title: "Нагрузочный тест на отдаленном контроле",
    items: [
      "Блок 8: добавлены поля для результатов нагрузочного теста на follow-up — тип теста, количественный результат и клинически значимая ишемия (Да/Нет). Это позволяет фиксировать отдаленный контроль без обязательной повторной госпитализации и ангиографии.",
      "Если нагрузочный тест не проводился, зависимые поля результата и значимой ишемии автоматически кодируются как N/A через общий тумблер «Авто N/A». В статистике появился готовый сценарий «Отдаленная ишемия: нагрузочный тест».",
    ],
  },
  {
    version: "0.6.0",
    date: "июль 2026",
    title: "Каскадные автозаполнения и формат процентов",
    items: [
      "Блок 5: если «Факт любого осложнения» = Нет, все конкретные осложнения (slow/no-reflow, дистальная эмболия, острый тромбоз, перфорация, спазм, дополнительные меры) автоматически проставляются как Нет и блокируются. Меняете гейт обратно на «Да» — поля разблокируются и очищаются для реального ввода.",
      "Блок 7: если «Экстренная коронарография» = Нет, поле «What on angio?» автоматически кодируется как 99 (N/A) — не оценивали, потому что не делали. Работает через тот же тумблер «Авто N/A», что и остальные гейты.",
      "Поля «Степень стеноза (QCA)» (Блок 2) и «Остаточный стеноз (QCA)» (Блок 6) теперь явно просят целое число процентов без знака «%» — раньше подсказка «Число (%)» оставляла место для сомнений, писать ли «60» или «60%». Уже введённые значения не трогаются.",
    ],
  },
  {
    version: "0.5.0",
    date: "июль 2026",
    title: "Канонизация названий артерий",
    items: [
      "Поле «Целевой сосуд» приводится к единому виду автоматически при уходе фокуса и при сохранении: ПМЖВ/ПНА → ПМЖА, ДА → ДВ, АТК → ВТК, ОВ → ОА, ЗМЖА → ЗМЖВ. Клинические синонимы (в рекомендациях встречаются все три «передних»: нисходящая / межжелудочковая артерия / межжелудочковая ветвь) больше не расщепляют один сосуд на несколько строк в аналитике.",
      "Артерии, не входящие в перечень (ПКА, ЛКА, СтЛКА и др.), сохраняются как введены — канонизатор их не трогает, чтобы не потерять валидные данные.",
    ],
  },
  {
    version: "0.4.0",
    date: "июль 2026",
    title: "Пост-ВСУЗИ гейт и кириллица в Excel",
    items: [
      "Блок 6: новое поле «Выполнялось ли ВСУЗИ ПОСЛЕ?». При «нет» поля MLA_Post и Dissection_IVUS сами кодируются как N/A (−99 / 99), руками -99 писать больше не нужно. Гейт по логике зеркалит пре-ВСУЗИ из блока 3. Для совместимости колонка IVUS_Post_Performed добавлена в конец экспорта — позиции остальных 99 колонок не сдвинулись.",
      "Экспорт CSV: добавлен UTF-8 BOM в data / clean dataset / codebook. Excel в русской локали раньше открывал файлы как Windows-1251 и портил кириллицу (ПМЖА / ПКА / диссекция и т.п.) — теперь читает как UTF-8, названия артерий переименовывать в LAD/RCA не надо.",
    ],
  },
  {
    version: "0.3.0",
    date: "июль 2026",
    title: "Автозаполнение Блока 1 и защита от пустых ячеек",
    items: [
      "Автозаполнение Блока 1 по Patient_ID: второе поражение того же пациента (А-001-2 после А-001-1) подтягивает клинико-анамнестические поля из предыдущего поражения. Заполняются только пустые поля; есть тумблер и кнопка ручного переноса.",
      "Авто N/A для непроводившихся процедур: при «процедура не проводилась» (например IVUS_Performed = 0) зависимые поля сами кодируются как N/A (категориальные — 99, количественные — −99) и блокируются. Вернул гейт в «Да» — код стирается, поля открываются.",
      "Статистика не считает коды N/A (никогда не усредняет −99), но в экспорт они попадают — база без «дыр» для SPSS/R.",
      "Экспорт для анализа: кнопка «Clean dataset» (аналитический срез, синтетика SYN-* исключается) и «Codebook» (расшифровка всех колонок и кодов, с колонкой NA_Code).",
    ],
  },
  {
    version: "0.2.0",
    date: "июнь 2026",
    title: "Общая защищённая база команды",
    items: [
      "Общая база на PocketBase: вход по логину/паролю, роли (врач/координатор), учёт автора записи и аудит-метки.",
      "Ежедневный бэкап базы; выгрузка CSV/JSON доступна в любой момент.",
      "Тёмная тема и мобильная версия (меню-бургер, адаптив).",
    ],
  },
  {
    version: "0.1.0",
    date: "июнь 2026",
    title: "Перенос на модульный стек",
    items: [
      "Переход с одного HTML-файла на Vite + TypeScript, покрытие формул и статистики unit-тестами.",
      "Исправлен баг в расчёте p-value (logGamma), из-за которого встроенная статистика считалась неверно.",
    ],
  },
];

export const CURRENT_VERSION = CHANGELOG[0].version;
